import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';

import React from 'react';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import classNames from 'clsx';

import { IconButton } from 'ui-components/components/button/icon-button';
import { FlexRow } from 'ui-components/components/flex';

import messages from './messages';
import style from './rate-recommendation.module.less';
import { RecommendationBannerContent } from './recommendation-banner-content';

const themeStyles = {
  light: {
    banner: style['recommendation-wrapper-light'],
    closeButton: style['recommendation-close-button-light'],
  },
  medium: {
    banner: style['recommendation-wrapper-medium'],
    closeButton: style['recommendation-close-button-medium'],
  },
  dark: {
    banner: style['recommendation-wrapper-dark'],
    closeButton: style['recommendation-close-button-dark'],
  },
};

/**
 * @typedef {import('../../../prop-types/currency').Currency} Currency
 */

/**
 * @typedef {import('ui-components/components/button').ButtonClickHandler} ButtonClickHandler
 */

/**
 * @param {object} props
 * @param {Currency} [props.appliedSavings] - The amount of savings that have been applied from this recommendation
 * @param {React.ReactNode} props.applyButton - The button to apply the recommendation.
 * @param {React.ReactNode} props.bannerText - The text to display in the banner.
 * @param {string} [props.className] - The className to apply to the banner.
 * @param {string} [props.contentClassName] - The className to apply to the banner content.
 * @param {boolean} [props.isApplyingSavings] - Whether savings are being applied.
 * @param {ButtonClickHandler} props.onDismiss - The function to call when the banner is dismissed.
 * @param {'light'|'medium'|'dark'} [props.theme] - The theme of the banner, either 'light', 'medium', or 'dark'.
 * @param {string} [props.loadingIndicatorContainerClassName]
 * @returns {React.JSX.Element}
 */
export const RateRecommendationBanner = ({
  appliedSavings,
  applyButton,
  bannerText,
  className,
  contentClassName,
  isApplyingSavings = false,
  onDismiss,
  theme = 'light',
  loadingIndicatorContainerClassName,
}) => {
  const stylesForTheme = themeStyles[theme] || themeStyles.light;
  const canDismiss = onDismiss && !isApplyingSavings && !appliedSavings?.value;

  return (
    <FlexRow className={classNames(style['recommendation-wrapper'], stylesForTheme.banner, className)}>
      <div className={style['recommendation-content-wrapper']}>
        <div className={style['recommendation-banner-icon-container']}>
          <FontAwesomeIcon className={style['recommendation-banner-sparkle-icon']} icon={faSparkles} />
        </div>
        <RecommendationBannerContent
          appliedSavings={appliedSavings}
          applyButton={applyButton}
          bannerText={bannerText}
          className={contentClassName}
          isApplyingSavings={isApplyingSavings}
          loadingIndicatorContainerClassName={loadingIndicatorContainerClassName}
          theme={theme}
        />
      </div>
      {canDismiss ? (
        <IconButton
          ariaLabelMessageDescriptor={messages.closeRateRecommendation}
          className={classNames(style['recommendation-close-button'], stylesForTheme.closeButton)}
          icon={faTimes}
          onClick={onDismiss}
        />
      ) : null}
    </FlexRow>
  );
};
